import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatFullName, formatName, getInitials } from '../../utils/nameUtils';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Badge,
  Alert,
  Divider,
  Backdrop,
  CircularProgress,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Pending as PendingIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Badge as LicenseIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useOrg } from '../../contexts/OrgContext';
import Breadcrumbs from '../../components/Breadcrumbs';
import { adminAPI, authAPI, resourcesAPI, repAPI, clientContactsAPI } from '../../services/api';

// Mock data - in real app, this would come from API
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

const ClientManagement = () => {
  const { org } = useOrg();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);
  const [viewStatus, setViewStatus] = useState('ACTIVE'); // 'ACTIVE' or 'INACTIVE'
  const [clients, setClients] = useState([]);
  const [signupRequests, setSignupRequests] = useState([]);
  const [createClientOpen, setCreateClientOpen] = useState(false);
  const [viewClientOpen, setViewClientOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [fetchingClientDetails, setFetchingClientDetails] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [reviewSignupOpen, setReviewSignupOpen] = useState(false);
  const [selectedSignupRequest, setSelectedSignupRequest] = useState(null);
  const [resources, setResources] = useState([]);
  const [uploadingResource, setUploadingResource] = useState(false);
  const [reps, setReps] = useState([]);
  const [loadingReps, setLoadingReps] = useState(false);
  const [clientReps, setClientReps] = useState({}); // Map of clientId -> rep relationship
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    phone: '',
    practiceName: '',
    medicalLicense: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    repId: '' // Added rep selection on create
  });
  const [createClientError, setCreateClientError] = useState('');
  
  // Contacts management for edit dialog
  const [clientContacts, setClientContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [newContact, setNewContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'VET', // VET, VET_TECH, BILLING
    hasPortalAccess: false,
    supervisingVetContactId: '',
  });
  // Edit contact state
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editContactData, setEditContactData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'VET',
    supervisingVetContactId: '',
  });
  const [editClientData, setEditClientData] = useState({
    name: '',
    email: '',
    phone: '',
    practiceName: '',
    medicalLicense: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    status: 'ACTIVE',
    repId: '', // Changed from repName to repId for dropdown
    internalNotes: '' // Internal-only notes
  });

  const organizationCode = org === 'bare' ? 'BARE' : 'EPL';

  const mapUserToClient = useCallback((user) => ({
    id: user.id,
    name: formatFullName(user) || user.email,
    email: user.email,
    phone: user.phone || 'N/A',
    practiceName: user.profile?.clinicName || '—',
    medicalLicense: user.profile?.licenseNumber || '—',
    status: (user.status || '').toUpperCase(),
    joinDate: user.createdAt ? new Date(user.createdAt) : null,
    lastOrder: user.lastLoginAt ? new Date(user.lastLoginAt) : null,
    totalOrders: user.totalOrders || 0,
    totalSpent: user.totalSpent || 0,
    city: user.profile?.city || '—',
    state: user.profile?.state || '—',
    repName: user.profile?.repId || '',
    repId: null, // Will be populated from clientReps
    internalNotes: user.internalNotes || user.profile?.internalNotes || null, // Internal-only notes
  }), []);

  const mapUserToSignup = useCallback((user) => ({
    id: user.id,
    name: formatFullName(user) || user.email,
    prefix: user.prefix || '',
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    suffix: user.suffix || '',
    email: user.email,
    phone: user.phone || 'N/A',
    practiceName: user.profile?.clinicName || '—',
    medicalLicense: user.profile?.licenseNumber || '—',
    address: user.profile?.address || '—',
    city: user.profile?.city || '—',
    state: user.profile?.state || '—',
    zipCode: user.profile?.zipCode || '—',
    submitDate: user.createdAt ? new Date(user.createdAt) : null,
    status: (user.status || '').toUpperCase(),
  }), []);

  const loadUsers = useCallback(async () => {
    try {
      const activeResponse = await adminAPI.getUsers({ status: viewStatus, organization: organizationCode, userType: 'CLIENT' });
      if (activeResponse.success && activeResponse.data?.users) {
        const mappedClients = activeResponse.data.users.map(mapUserToClient);
        setClients(mappedClients);
        // Load rep assignments for all clients
        const clientIds = mappedClients.map(c => c.id);
        await loadClientReps(clientIds);
      } else {
        setClients([]);
      }

      const pendingResponse = await adminAPI.getUsers({ status: 'PENDING_APPROVAL', organization: organizationCode, userType: 'CLIENT' });
      if (pendingResponse.success && pendingResponse.data?.users) {
        setSignupRequests(pendingResponse.data.users.map(mapUserToSignup));
      } else {
        setSignupRequests([]);
      }
    } catch (error) {
      console.error('Failed to load client data:', error);
    }
  }, [organizationCode, mapUserToClient, mapUserToSignup, viewStatus]);

  useEffect(() => {
    loadUsers();
    loadResources();
    loadReps();
  }, [loadUsers, viewStatus]);

  const loadReps = async () => {
    try {
      setLoadingReps(true);
      const response = await repAPI.getReps();
      if (response.success && response.data) {
        setReps(response.data);
      }
    } catch (error) {
      console.error('Failed to load reps:', error);
    } finally {
      setLoadingReps(false);
    }
  };

  const loadClientReps = async (clientIds) => {
    if (!clientIds || clientIds.length === 0) return;
    
    try {
      const repPromises = clientIds.map(async (clientId) => {
        try {
          const response = await repAPI.getClientRep(clientId);
          if (response.success && response.data) {
            return { clientId, rep: response.data };
          }
        } catch (error) {
          console.error(`Failed to load rep for client ${clientId}:`, error);
        }
        return { clientId, rep: null };
      });

      const results = await Promise.all(repPromises);
      const repMap = {};
      results.forEach(({ clientId, rep }) => {
        repMap[clientId] = rep;
      });
      setClientReps(prev => ({ ...prev, ...repMap }));
    } catch (error) {
      console.error('Failed to load client reps:', error);
    }
  };

  const loadResources = async () => {
    try {
      const response = await resourcesAPI.listResources();
      if (response.success && response.data) {
        setResources(response.data);
      }
    } catch (error) {
      console.error('Failed to load resources:', error);
    }
  };

  const handleUploadResource = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 
                          'image/jpeg', 'image/png', 'image/jpg', 'text/plain'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Allowed types: PDF, DOC, DOCX, JPG, PNG, TXT');
      return;
    }

    // Validate file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      alert('File size must be less than 20MB');
      return;
    }

    try {
      setUploadingResource(true);
      const response = await resourcesAPI.uploadResource(file);
      if (response.success) {
        await loadResources();
        alert('Resource uploaded successfully');
        event.target.value = ''; // Reset file input
      }
    } catch (error) {
      console.error('Failed to upload resource:', error);
      alert(`Failed to upload resource: ${error.message}`);
    } finally {
      setUploadingResource(false);
    }
  };

  const handleDeleteResource = async (resourceName) => {
    if (!window.confirm(`Are you sure you want to delete "${resourceName}"?`)) {
      return;
    }

    try {
      const response = await resourcesAPI.deleteResource(resourceName);
      if (response.success) {
        await loadResources();
        alert('Resource deleted successfully');
      }
    } catch (error) {
      console.error('Failed to delete resource:', error);
      alert(`Failed to delete resource: ${error.message}`);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (contentType) => {
    if (contentType.includes('pdf')) return '📄';
    if (contentType.includes('word') || contentType.includes('document')) return '📝';
    if (contentType.includes('image')) return '🖼️';
    return '📎';
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCreateClient = async () => {
    setCreateClientError('');
    try {
      const organizationCode = org === 'bare' ? 'BARE' : 'EPL';
      const [firstName = '', ...rest] = (newClient.name || '').trim().split(' ');
      const lastName = rest.join(' ') || 'Client';

      const payload = {
        firstName,
        lastName,
        email: newClient.email,
        phone: newClient.phone,
        clinicName: newClient.practiceName,
        address: newClient.address,
        city: newClient.city,
        state: newClient.state,
        zipCode: newClient.zipCode,
        medicalLicenseNumber: newClient.medicalLicense,
        organization: organizationCode,
        userType: 'CLIENT',
      };

      const response = await authAPI.register(payload);
      if (response?.success) {
        // If a rep was selected, assign them to the new client
        const newClientId = response.data?.user?.id;
        if (newClient.repId && newClientId) {
          try {
            await repAPI.assignClient({
              repUserId: newClient.repId,
              clientUserId: newClientId,
              notes: 'Rep assigned during client creation'
            });
          } catch (repError) {
            console.error('Failed to assign rep:', repError);
            // Don't fail the whole operation, just log
          }
        }
        
        await loadUsers();
        setNewClient({
          name: '',
          email: '',
          phone: '',
          practiceName: '',
          medicalLicense: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          repId: ''
        });
        setCreateClientError('');
        setCreateClientOpen(false);
      }
    } catch (error) {
      console.error('Failed to create client:', error);
      const errorMessage = error?.message || 'Failed to create client account';
      if (errorMessage.includes('already exists') || errorMessage.includes('email') || errorMessage.includes('409')) {
        setCreateClientError(`An account with the email "${newClient.email}" already exists. Please use a different email address or check if this client is already in the system.`);
      } else {
        setCreateClientError(errorMessage);
      }
    }
  };

  const handleReviewSignup = (request) => {
    setSelectedSignupRequest(request);
    setReviewSignupOpen(true);
  };

  const handleApproveSignup = async (userId) => {
    try {
      const response = await adminAPI.updateUserStatus(userId, 'ACTIVE');
      if (response.success) {
        await loadUsers();
        setReviewSignupOpen(false);
        setSelectedSignupRequest(null);
      } else {
        console.error("Failed to approve signup:", response.error);
      }
    } catch (err) {
      console.error("Error approving signup:", err);
    }
  };

  const handleRejectSignup = async (userId) => {
    try {
      const reason = window.prompt('Provide a reason for rejection (optional)') || undefined;
      const response = await adminAPI.updateUserStatus(userId, 'REJECTED', reason);
      if (response.success) {
        await loadUsers();
        setReviewSignupOpen(false);
        setSelectedSignupRequest(null);
      }
    } catch (err) {
      console.error("Error rejecting signup:", err);
    }
  };

  const handleViewClient = async (client) => {
    // Fetch full client data including internal notes
    try {
      const response = await adminAPI.getUserById(client.id);
      if (response.success && response.data) {
        const fullClient = mapUserToClient(response.data);
        setSelectedClient(fullClient);
      } else {
        setSelectedClient(client);
      }
    } catch (error) {
      console.error('Failed to load client details:', error);
      setSelectedClient(client);
    }
    setViewClientOpen(true);
  };

  // Load contacts for a client directly
  const loadClientContacts = async (clientId) => {
    setLoadingContacts(true);
    setClientContacts([]);
    
    try {
      const response = await clientContactsAPI.getContacts(clientId);
      if (response.success && response.data) {
        setClientContacts(response.data);
      } else {
        setClientContacts([]);
      }
    } catch (error) {
      console.error('Failed to load contacts:', error);
      setClientContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleAddContact = () => {
        setNewContact({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          role: 'VET',
          hasPortalAccess: false,
          supervisingVetContactId: '',
        });
    setAddContactOpen(true);
  };

  const handleSaveNewContact = async () => {
    if (!newContact.firstName || !newContact.lastName || !newContact.email) {
      alert('Please fill in first name, last name, and email');
      return;
    }
    
    if (!editingClient?.id) {
      alert('No client selected');
      return;
    }
    
    setSavingContact(true);
    try {
      // Add contact via API - linked directly to client user ID
      const response = await clientContactsAPI.addContact({
        clientUserId: editingClient.id,
        firstName: newContact.firstName,
        lastName: newContact.lastName,
        email: newContact.email,
        phone: newContact.phone,
        role: newContact.role,
        hasPortalAccess: newContact.hasPortalAccess,
        canPlaceOrders: newContact.hasPortalAccess,
        supervisingVetContactId: newContact.supervisingVetContactId || null,
      });
      
      if (response.success && response.data) {
        setClientContacts(prev => [...prev, response.data]);
        setAddContactOpen(false);
        // Show success message for portal access
        if (newContact.hasPortalAccess) {
          alert('Contact added! They can now log in using a magic link sent to their email.');
        }
      } else {
        throw new Error(response.message || 'Failed to add contact');
      }
    } catch (error) {
      console.error('Failed to add contact:', error);
      alert('Failed to add contact: ' + error.message);
    } finally {
      setSavingContact(false);
    }
  };

  const handleRemoveContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to remove this contact? This will also revoke their portal access.')) {
      return;
    }
    
    try {
      const response = await clientContactsAPI.removeContact(contactId);
      if (response.success) {
        setClientContacts(prev => prev.filter(c => c.id !== contactId));
      } else {
        throw new Error(response.message || 'Failed to remove contact');
      }
    } catch (error) {
      console.error('Failed to remove contact:', error);
      alert('Failed to remove contact: ' + error.message);
    }
  };

  const handleTogglePortalAccess = async (contactId) => {
    const contact = clientContacts.find(c => c.id === contactId);
    if (!contact) return;
    
    const newAccess = !contact.hasPortalAccess;
    
    try {
      const response = await clientContactsAPI.updatePortalAccess(contactId, {
        hasPortalAccess: newAccess,
        canPlaceOrders: newAccess,
      });
      
      if (response.success) {
        setClientContacts(prev => prev.map(c => 
          c.id === contactId ? { ...c, hasPortalAccess: newAccess } : c
        ));
        if (newAccess) {
          alert('Portal access enabled! Contact can now log in using a magic link.');
        }
      } else {
        throw new Error(response.message || 'Failed to update portal access');
      }
    } catch (error) {
      console.error('Failed to update portal access:', error);
      alert('Failed to update portal access: ' + error.message);
    }
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setEditContactData({
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      email: contact.email || '',
      phone: contact.phone || '',
      role: contact.role || 'VET',
      supervisingVetContactId: contact.supervisingVetContactId || '',
    });
    setEditContactOpen(true);
  };

  const handleSaveEditedContact = async () => {
    if (!editContactData.firstName || !editContactData.lastName || !editContactData.email) {
      alert('Please fill in first name, last name, and email');
      return;
    }
    
    if (!editingContact?.id) {
      alert('No contact selected');
      return;
    }
    
    setSavingContact(true);
    try {
      const response = await clientContactsAPI.updateContact(editingContact.id, {
        firstName: editContactData.firstName,
        lastName: editContactData.lastName,
        email: editContactData.email,
        phone: editContactData.phone || null,
        role: editContactData.role,
        supervisingVetContactId: editContactData.supervisingVetContactId || null,
      });
      
      if (response.success && response.data) {
        // Update the contact in local state
        setClientContacts(prev => prev.map(c => 
          c.id === editingContact.id ? response.data : c
        ));
        setEditContactOpen(false);
        setEditingContact(null);
      } else {
        throw new Error(response.message || 'Failed to update contact');
      }
    } catch (error) {
      console.error('Failed to update contact:', error);
      alert('Failed to update contact: ' + error.message);
    } finally {
      setSavingContact(false);
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'VET': return 'Veterinarian';
      case 'VET_TECH': return 'Vet Tech';
      case 'BILLING': return 'Billing Contact';
      default: return role;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'VET': return '#039582';
      case 'VET_TECH': return '#8b5cf6';
      case 'BILLING': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const handleEditClient = async (client) => {
    // Navigate to detail page instead of opening modal
    navigate(`/internal/${org}/clients/${client.id}`);
  };

  const handleSaveClient = async () => {
    if (!editingClient) return;

    try {
      setSavingClient(true);
      const [firstName = '', ...rest] = (editClientData.name || '').trim().split(' ');
      const lastName = rest.join(' ') || 'Client';

      const payload = {
        firstName,
        lastName,
        email: editClientData.email,
        phone: editClientData.phone,
        status: editClientData.status,
        internalNotes: editClientData.internalNotes || null, // Internal-only notes
        profile: {
          clinicName: editClientData.practiceName,
          licenseNumber: editClientData.medicalLicense,
          address: editClientData.address,
          city: editClientData.city,
          state: editClientData.state,
          zipCode: editClientData.zipCode,
        }
      };

      const response = await adminAPI.updateUser(editingClient.id, payload);
      if (response.success) {
        // Update rep assignment if changed - fetch current rep first to ensure accuracy
        let currentRepId = '';
        try {
          const repResponse = await repAPI.getClientRep(editingClient.id);
          if (repResponse.success && repResponse.data?.rep) {
            currentRepId = repResponse.data.rep.id;
          }
        } catch (error) {
          console.error('Failed to fetch current rep:', error);
        }
        
        if (editClientData.repId !== currentRepId) {
          if (editClientData.repId) {
            // Assign or reassign rep
            if (currentRepId) {
              // Reassign
              try {
                await repAPI.reassignClient(editingClient.id, {
                  newRepUserId: editClientData.repId,
                  notes: 'Rep assignment updated by admin'
                });
              } catch (reassignError) {
                console.error('Reassign failed, trying direct assign:', reassignError);
                // Fallback: deactivate old and create new
                await repAPI.assignClient({
                  repUserId: editClientData.repId,
                  clientUserId: editingClient.id,
                  notes: 'Rep assigned by admin'
                });
              }
            } else {
              // New assignment
              await repAPI.assignClient({
                repUserId: editClientData.repId,
                clientUserId: editingClient.id,
                notes: 'Rep assigned by admin'
              });
            }
          } else if (currentRepId) {
            // Unassign - deactivate relationship
            try {
              await repAPI.reassignClient(editingClient.id, {
                newRepUserId: null,
                notes: 'Rep unassigned by admin'
              });
            } catch (unassignError) {
              console.error('Unassign failed:', unassignError);
            }
          }
          
          // Reload client rep assignments to ensure UI is updated
          await loadClientReps([editingClient.id]);
          // Also reload users to refresh the list
          await loadUsers();
        }
        
        await loadUsers();
        setEditClientOpen(false);
        setEditingClient(null);
        setEditClientData({
          name: '',
          email: '',
          phone: '',
          practiceName: '',
          medicalLicense: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          status: 'ACTIVE',
          repId: ''
        });
      } else {
        alert('Failed to update client: ' + (response.error || response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to update client:', error);
      alert('Failed to update client: ' + (error.message || 'Unknown error'));
    } finally {
      setSavingClient(false);
    }
  };

  const getStatusColor = (status) => {
    const normalized = (status || '').toUpperCase();
    switch (normalized) {
      case 'ACTIVE': return '#10b981';
      case 'INACTIVE': return '#ef4444';
      case 'PENDING':
      case 'PENDING_APPROVAL':
      case 'PENDING_VERIFICATION': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const formatStatusLabel = (status) => {
    const normalized = (status || '').toUpperCase();
    switch (normalized) {
      case 'ACTIVE': return 'Active';
      case 'INACTIVE': return 'Inactive';
      case 'PENDING_APPROVAL': return 'Pending Approval';
      case 'PENDING_VERIFICATION': return 'Pending Verification';
      case 'REJECTED': return 'Rejected';
      default: return normalized || 'Unknown';
    }
  };

  const formatCurrency = (value) => `$${value?.toLocaleString() || 0}`;

  return (
    <Box>
      <Breadcrumbs />
      {/* Header */}
      <Box sx={{
        mb: 3,
        mt: { xs: '50px', md: 0 }, // push down on mobile to sit below hamburger
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        pl: { xs: '16px', md: 3 },
        transition: 'margin 0.2s ease',
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
          Client Management
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
        Enterprise-grade client management and tracking
        </Typography>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2, border: '1px solid #e5e7eb' }}>
            <Avatar sx={{ bgcolor: '#10b981', mx: 'auto', mb: 1 }}>
              <PeopleIcon />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5 }}>
              {Array.isArray(clients) ? clients.filter(c => c.status === 'ACTIVE').length : 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active Clients
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2, border: '1px solid #e5e7eb' }}>
            <Avatar sx={{ bgcolor: '#f59e0b', mx: 'auto', mb: 1 }}>
              <PendingIcon />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5 }}>
              {signupRequests.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pending Signups
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2, border: '1px solid #e5e7eb' }}>
            <Avatar sx={{ bgcolor: '#8b5cf6', mx: 'auto', mb: 1 }}>
              <BusinessIcon />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5 }}>
              {Array.isArray(clients) ? clients.reduce((sum, c) => sum + (c.totalOrders || 0), 0) : 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Orders
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2, border: '1px solid #e5e7eb' }}>
            <Avatar sx={{ bgcolor: '#06b6d4', mx: 'auto', mb: 1 }}>
              <EmailIcon />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5 }}>
              {formatCurrency(Array.isArray(clients) ? clients.reduce((sum, c) => sum + (c.totalSpent || 0), 0) : 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Revenue
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon fontSize="small" />
                  Manage Clients ({Array.isArray(clients) ? clients.length : 0})
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PendingIcon fontSize="small" />
                  Signup Requests
                  <Badge badgeContent={signupRequests.length} color="warning" />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AddIcon fontSize="small" />
                  Client Resources
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {tabValue === 0 && (
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {viewStatus === 'ACTIVE' ? 'Active' : 'Inactive'} {org === 'bare' ? 'Bare Biologics' : 'EPL'} Clients
                </Typography>
                <ToggleButtonGroup
                  value={viewStatus}
                  exclusive
                  onChange={(e, newStatus) => {
                    if (newStatus) setViewStatus(newStatus);
                  }}
                  size="small"
                  sx={{ 
                    height: 32,
                    '& .MuiToggleButton-root': {
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      margin: 0,
                      '&:not(:first-of-type)': {
                        borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
                        marginLeft: -1, // Overlap borders to prevent double thickness
                      },
                      '&.Mui-selected': {
                        border: '1px solid transparent', // Remove conflicting border
                        '&:not(:first-of-type)': {
                          marginLeft: -1, // Maintain overlap
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton 
                    value="ACTIVE" 
                    sx={{ 
                      textTransform: 'none', 
                      fontWeight: 600,
                      px: 2,
                      '&.Mui-selected': {
                        bgcolor: '#10b981',
                        color: 'white',
                        borderColor: '#10b981 !important', // Force border color
                        zIndex: 1, // Bring to front
                        '&:hover': {
                          bgcolor: '#059669'
                        }
                      }
                    }}
                  >
                    Active
                  </ToggleButton>
                  <ToggleButton 
                    value="INACTIVE" 
                    sx={{ 
                      textTransform: 'none', 
                      fontWeight: 600,
                      px: 2,
                      '&.Mui-selected': {
                        bgcolor: '#ef4444',
                        color: 'white',
                        borderColor: '#ef4444 !important', // Force border color
                        zIndex: 1, // Bring to front
                        '&:hover': {
                          bgcolor: '#dc2626'
                        }
                      }
                    }}
                  >
                    Inactive
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setCreateClientOpen(true)}
                sx={{
                  bgcolor: '#039582',
                  color: 'white',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    bgcolor: '#027A6B',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Create New Client
              </Button>
            </Box>
            {isMobile ? (
              <Stack spacing={1.5} sx={{ p: 1 }}>
                {Array.isArray(clients) ? clients.map((client) => (
                  <Paper key={client.id} variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#039582' }}>
                          {(client.practiceName || 'P').charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{client.practiceName || 'No Practice Name'}</Typography>
                          <Typography variant="caption" color="text.secondary">Contact: {client.name}</Typography>
                        </Box>
                      </Box>
                      <Chip label={formatStatusLabel(client.status)} size="small" sx={{ bgcolor: getStatusColor(client.status), color: 'white', fontWeight: 600 }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">{client.email}</Typography>
                      <Typography variant="body2" color="text.secondary">License: {client.medicalLicense}</Typography>
                      <Typography variant="body2" color="text.secondary">{client.city}, {client.state}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Rep: {(() => {
                          const rep = clientReps[client.id];
                          return rep?.rep ? (formatFullName(rep.rep) || rep.rep.email || 'Unknown') : '—';
                        })()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Orders: {client.totalOrders}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Spent: {formatCurrency(client.totalSpent)}</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewClient(client)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View/Edit Client">
                          <IconButton size="small" onClick={() => handleEditClient(client)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Paper>
                )) : null}
              </Stack>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', overflowX: 'auto' }}>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Practice</strong></TableCell>
                      <TableCell><strong>Primary Contact</strong></TableCell>
                      <TableCell><strong>Location</strong></TableCell>
                      <TableCell><strong>Rep</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Orders</strong></TableCell>
                      <TableCell><strong>Total Spent</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(clients) ? clients.map((client) => (
                      <TableRow key={client.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: '#039582' }}>
                              {(client.practiceName || 'P').charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {client.practiceName || 'No Practice Name'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                License: {client.medicalLicense}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {client.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {client.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {client.city}, {client.state}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {(() => {
                              const rep = clientReps[client.id];
                              if (rep?.rep) {
                                return formatFullName(rep.rep) || rep.rep.email || 'Unknown';
                              }
                              return '—';
                            })()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatStatusLabel(client.status)}
                            size="small"
                            sx={{ bgcolor: getStatusColor(client.status), color: 'white', fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {client.totalOrders}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(client.totalSpent)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="View Details">
                              <IconButton size="small" onClick={() => handleViewClient(client)}>
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Client">
                              <IconButton size="small" onClick={() => handleEditClient(client)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        )}

        {tabValue === 2 && (
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Client Resources
              </Typography>
              <label htmlFor="upload-resource">
                <input
                  id="upload-resource"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleUploadResource}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                  disabled={uploadingResource}
                />
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<AddIcon />}
                  disabled={uploadingResource}
                  sx={{
                    bgcolor: '#039582',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: '#027A6B',
                    }
                  }}
                >
                  {uploadingResource ? 'Uploading...' : 'Upload Resource'}
                </Button>
              </label>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload documents that will be visible to all clients in the client portal under the Resources tab.
            </Typography>
            {resources.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AddIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No resources uploaded yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload documents to make them available to clients
                </Typography>
              </Box>
            ) : (
              isMobile ? (
                <Stack spacing={1.5} sx={{ p: 1 }}>
                  {resources.map((resource) => (
                    <Paper key={resource.name} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6">{getFileIcon(resource.contentType)}</Typography>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{resource.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(resource.size)} • {new Date(resource.lastModified).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteResource(resource.name)}
                          sx={{ color: '#dc2626' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Resource</strong></TableCell>
                        <TableCell><strong>Type</strong></TableCell>
                        <TableCell><strong>Size</strong></TableCell>
                        <TableCell><strong>Last Modified</strong></TableCell>
                        <TableCell><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resources.map((resource) => (
                        <TableRow key={resource.name} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="h6">{getFileIcon(resource.contentType)}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {resource.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {resource.contentType}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatFileSize(resource.size)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(resource.lastModified).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="View/Download">
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(resource.url, '_blank')}
                                  sx={{ color: '#039582' }}
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteResource(resource.name)}
                                  sx={{ color: '#dc2626' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            )}
          </CardContent>
        )}

        {tabValue === 1 && (
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Pending {org === 'bare' ? 'Bare Biologics' : 'EPL'} Signup Requests
            </Typography>
            {signupRequests.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PendingIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No pending signup requests
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  New client registrations will appear here for review
                </Typography>
              </Box>
            ) : (
              isMobile ? (
                <Stack spacing={1.5} sx={{ p: 1 }}>
                  {signupRequests.map((request) => (
                    <Paper key={request.id} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {request.name.split(' ').map(n => n[0]).join('')}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{request.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{request.email}</Typography>
                          </Box>
                        </Box>
                        <Chip label={formatStatusLabel(request.status)} size="small" sx={{ bgcolor: getStatusColor(request.status), color: 'white', fontWeight: 600 }} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">{request.practiceName}</Typography>
                        <Typography variant="body2" color="text.secondary">License: {request.medicalLicense}</Typography>
                        <Typography variant="body2" color="text.secondary">{request.city}, {request.state}</Typography>
                        <Typography variant="body2" color="text.secondary">Submitted: {request.submitDate ? request.submitDate.toLocaleDateString() : '—'}</Typography>
                      </Box>
                      <Button size="small" variant="contained" startIcon={<ViewIcon />} onClick={() => handleReviewSignup(request)} sx={{ minWidth: 'auto' }}>
                        Review
                      </Button>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Applicant</strong></TableCell>
                        <TableCell><strong>Practice Details</strong></TableCell>
                        <TableCell><strong>Location</strong></TableCell>
                        <TableCell><strong>Submit Date</strong></TableCell>
                        <TableCell><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {signupRequests.map((request) => (
                        <TableRow key={request.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 32, height: 32 }}>
                                {request.name.split(' ').map(n => n[0]).join('')}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {request.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {request.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {request.practiceName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              License: {request.medicalLicense}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {request.city}, {request.state}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {request.submitDate ? request.submitDate.toLocaleDateString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Review Application">
                                <Button size="small" variant="contained" startIcon={<ViewIcon />} onClick={() => handleReviewSignup(request)} sx={{ minWidth: 'auto' }}>
                                  Review
                                </Button>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            )}
          </CardContent>
        )}
      </Card>

      {/* Create Client Dialog */}
      <Dialog open={createClientOpen} onClose={() => { setCreateClientOpen(false); setCreateClientError(''); }} maxWidth="md" fullWidth>
        <DialogTitle>Create New {org === 'bare' ? 'Bare Biologics' : 'EPL'} Client Account</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              This client will be added to the <strong>{org === 'bare' ? 'Bare Biologics' : 'EPL'}</strong> platform with access to all available products and services.
            </Alert>
            {createClientError && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setCreateClientError('')}>
                {createClientError}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Practice Name"
                  value={newClient.practiceName}
                  onChange={(e) => setNewClient({ ...newClient, practiceName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Medical License Number"
                  value={newClient.medicalLicense}
                  onChange={(e) => setNewClient({ ...newClient, medicalLicense: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Assign Sales Rep</InputLabel>
                  <Select
                    value={newClient.repId || ''}
                    onChange={(e) => setNewClient({ ...newClient, repId: e.target.value })}
                    disabled={loadingReps}
                    label="Assign Sales Rep"
                  >
                    <MenuItem value="">
                      <em>No Rep Assigned</em>
                    </MenuItem>
                    {reps.map((rep) => (
                      <MenuItem key={rep.id} value={rep.id}>
                        {formatFullName(rep) || rep.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={newClient.address}
                  onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={newClient.city}
                  onChange={(e) => setNewClient({ ...newClient, city: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  select
                  label="State"
                  value={newClient.state}
                  onChange={(e) => setNewClient({ ...newClient, state: e.target.value })}
                  required
                >
                  {US_STATES.map((state) => (
                    <MenuItem key={state.value} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={newClient.zipCode}
                  onChange={(e) => setNewClient({ ...newClient, zipCode: e.target.value })}
                  required
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateClientOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateClient}
            variant="contained"
            disabled={!newClient.name || !newClient.email}
          >
            Create Client Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Client Dialog */}
      <Dialog open={viewClientOpen} onClose={() => setViewClientOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Client Details</DialogTitle>
        <DialogContent>
          {selectedClient && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 64, height: 64 }}>
                  {selectedClient.name.split(' ').map(n => n[0]).join('')}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedClient.name}
                  </Typography>
                  <Typography color="text.secondary">
                    {selectedClient.email}
                  </Typography>
                  <Chip
                    label={selectedClient.status}
                    size="small"
                    sx={{
                      bgcolor: getStatusColor(selectedClient.status),
                      color: 'white',
                      fontWeight: 600,
                      mt: 0.5
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Practice</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedClient.practiceName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Medical License</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedClient.medicalLicense}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedClient.phone}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedClient.city}, {selectedClient.state}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Join Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedClient.joinDate ? selectedClient.joinDate.toLocaleDateString() : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Last Order</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedClient.lastOrder ? selectedClient.lastOrder.toLocaleDateString() : 'Never'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Total Orders</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedClient.totalOrders}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Total Spent</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatCurrency(selectedClient.totalSpent)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Assigned Rep</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {(() => {
                      const rep = clientReps[selectedClient.id];
                      if (rep?.rep) {
                        return formatFullName(rep.rep) || rep.rep.email || 'Unknown Rep';
                      }
                      return 'No Rep Assigned';
                    })()}
                  </Typography>
                </Grid>
                {selectedClient.internalNotes && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Internal Notes
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedClient.internalNotes}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewClientOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Client Dialog - DEPRECATED: Client editing now happens on the detail page */}
      {/* Navigate to /internal/{org}/clients/:id to edit clients */}
      
      {/* Add/Edit Contact Dialogs - DEPRECATED: Contact management now happens on the detail page */}
      {/* Navigate to /internal/{org}/clients/:id to manage contacts */}

      {/* Review Signup Request Dialog */}
      <Dialog open={reviewSignupOpen} onClose={() => setReviewSignupOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PendingIcon color="warning" />
            Review Signup Application
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSignupRequest && (
            <Box sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Please review this signup application carefully. Once approved, the applicant will receive login credentials and full access to the {org === 'bare' ? 'Bare Biologics' : 'EPL'} platform.
              </Alert>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 64, height: 64 }}>
                  {getInitials(selectedSignupRequest)}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedSignupRequest.name}
                  </Typography>
                  <Typography color="text.secondary">
                    {selectedSignupRequest.email}
                  </Typography>
                  <Chip
                    label="Pending Review"
                    size="small"
                    sx={{
                      bgcolor: '#f59e0b',
                      color: 'white',
                      fontWeight: 600,
                      mt: 0.5
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon />
                Personal Information
              </Typography>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                {selectedSignupRequest.prefix && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Prefix</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedSignupRequest.prefix}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>First Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedSignupRequest.firstName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Last Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedSignupRequest.lastName}
                  </Typography>
                </Grid>
                {selectedSignupRequest.suffix && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Suffix</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedSignupRequest.suffix}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon />
                Practice Information
              </Typography>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Practice Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedSignupRequest.practiceName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Medical License</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedSignupRequest.medicalLicense}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon />
                Contact Information
              </Typography>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Phone Number</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedSignupRequest.phone}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Email Address</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedSignupRequest.email}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon />
                Practice Address
              </Typography>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Street Address</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedSignupRequest.address}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>City</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedSignupRequest.city}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>State</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedSignupRequest.state}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>ZIP Code</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedSignupRequest.zipCode}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LicenseIcon />
                Application Details
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Submit Date</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {new Date(selectedSignupRequest.submitDate).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Application Status</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedSignupRequest.status}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={() => setReviewSignupOpen(false)}
            variant="outlined"
            size="small"
          >
            Close Review
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={() => handleRejectSignup(selectedSignupRequest?.id)}
              variant="outlined"
              color="error"
              startIcon={<RejectIcon />}
              size="small"
            >
              Reject Application
            </Button>
            <Button
              onClick={() => handleApproveSignup(selectedSignupRequest?.id)}
              variant="contained"
              color="success"
              startIcon={<ApproveIcon />}
              size="small"
            >
              Approve & Create Account
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientManagement;